import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import './frontend/index.css';
import { isGated } from './waitlist/gate';
import { WaitlistPage } from './waitlist/WaitlistPage';
import { ErrorBoundary, DarkFallback } from './ErrorBoundary';
import { ReadingHome } from './frontend/pages/ReadingHome';
import { isTvApp } from './frontend/reading/account';

// Initialize client-side Sentry for crash reporting
const SENTRY_DSN = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_SENTRY_DSN) || '';
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: (import.meta as any).env?.MODE || 'production',
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event) {
      if (event.exception?.values?.some(v => v.type === 'ChunkLoadError')) return null;
      return event;
    },
  });
}

// Analytics lives in its own module so pages can import it without a cycle.
export { trackEvent } from './frontend/analytics';
import { trackEvent } from './frontend/analytics';

if (typeof window !== 'undefined') {
  trackEvent('session_start', { path: window.location.pathname });
  if (isTvApp()) document.documentElement.classList.add('wallos-tv');
}

const TermsPage = React.lazy(() => import('./frontend/pages/legal/TermsPage'));
const DmcaPage = React.lazy(() => import('./frontend/pages/legal/DmcaPage'));
const PrivacyPage = React.lazy(() => import('./frontend/pages/legal/PrivacyPage'));
const CheckoutDonePage = React.lazy(() => import('./frontend/pages/CheckoutDonePage'));
const ReadAccountPage = React.lazy(() => import('./frontend/pages/read/ReadAccountPage'));
const TvPairPage = React.lazy(() => import('./frontend/pages/read/TvPairPage'));
const ConnectToTvPair = () => <Navigate to={`/read/tv${window.location.search}`} replace />;
const Landing = React.lazy(() => import('./frontend/pages/Landing').then((m) => ({ default: m.Landing })));

const lazyPage = (Page: React.LazyExoticComponent<React.ComponentType>) => (
  <React.Suspense fallback={<DarkFallback />}><Page /></React.Suspense>
);

// ── Pre-launch gate ─────────────────────────────────────────────────────────
// While src/waitlist/gate.ts has WAITLIST_GATE on, public visitors get the
// reading homepage, the old Wall OS landing (/os), the legal pages and the
// waitlist page for everything else. The full app bundle is never downloaded.
// See gate.ts for the tester invite link and exemptions (apps, localhost).
const root = ReactDOM.createRoot(document.getElementById('root')!);

if (isGated()) {
  root.render(
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<ReadingHome unlocked={false} />} />
          <Route path="/os" element={lazyPage(Landing)} />
          <Route path="/features" element={lazyPage(Landing)} />
          <Route path="/creators" element={lazyPage(Landing)} />
          <Route path="/download" element={lazyPage(Landing)} />
          {/* Copyright notices and store reviewers must always reach these. */}
          <Route path="/terms" element={lazyPage(TermsPage)} />
          <Route path="/dmca" element={lazyPage(DmcaPage)} />
          <Route path="/privacy" element={lazyPage(PrivacyPage)} />
          <Route path="/checkout/done" element={lazyPage(CheckoutDonePage)} />
          {/* Pairing a TV from a phone browser (QR scan, wallos.io/connect) works for every account. */}
          <Route path="/connect" element={<ConnectToTvPair />} />
          <Route path="/read/tv" element={lazyPage(TvPairPage)} />
          <Route path="/read/account" element={lazyPage(ReadAccountPage)} />
          <Route path="*" element={<WaitlistPage />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>,
  );
} else {
  import('./AppRoutes').then(({ AppRoutes }) => root.render(<AppRoutes />));
}
